import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import CommonHeadingNBtns from "../../common-components/CommonHeadingNBtns";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import { add_update_delete_dash_feature } from "../../../data/ApiEndpoints";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import SelectDropdown from "../../components-react/SelectDropdown";
import {
  fetchDashboardFeatureGroupingdata,
  fetchDashboardMenusdata,
} from "../../../redux/dashboard-features/featuresSlice";
import MultiSelect from "../../components-react/MultiSelect";

function validateForm(formValues) {
  let errors = [];

  formValues.forEach((items, index) => {
    let itemErrors = {};

    if (!items?.selectedMenu) {
      itemErrors.selectedMenu = `required!`;
    }

    if (!items?.featureName) {
      itemErrors.featureName = `required!`;
    }

    const routePathRegex = /^\/[a-zA-Z0-9\-_\/]*$/;
    if (!items?.featureRoute) {
      itemErrors.featureRoute = `required!`;
    } else if (!routePathRegex.test(items.featureRoute)) {
      itemErrors.featureRoute = `Invalid route path! A valid route starts with "/" and contains only alphanumeric characters, "-", "_", or "/".`;
    }

    if (!items?.sequenceNumber) {
      itemErrors.sequenceNumber = `required!`;
    }

    if (Object.keys(itemErrors).length > 0) {
      errors[index] = itemErrors;
    }
  });

  return errors;
}

const DashFeaturesForm = ({
  userInfoData,
  closeModal,
  callFunction,
  isEdit,
  rowData,
}) => {
  const dispatch = useDispatch();
  const { postPutLoading } = useSelector((state) => state.common);
  const {
    dashboardMenus,
    dashboardMenusLoading,
    dashFeatureGrouping,
    dashFeatureGroupingLoading,
  } = useSelector((state) => state.features);

  const [allDashMenus, setAllDashMenus] = useState([]);
  const [featureGroups, setFeatureGroups] = useState([]);
  const iniFormvaluesRaw = {
    selectedMenu: "",
    featureName: "",
    featureRoute: "",
    sequenceNumber: "",
    selectedFeatureGroups: [],
    isActive: false,
  };
  const iniFormvalues = {
    selectedMenu:
      isEdit && rowData?.menu_name && rowData?.menu_id
        ? {
            label: rowData.menu_name,
            value: rowData.menu_id,
          }
        : "",
    featureName: isEdit && rowData?.name ? rowData.name : "",
    featureRoute: isEdit && rowData?.route ? rowData.route : "",
    sequenceNumber: isEdit && rowData?.sequence_no ? rowData.sequence_no : "",
    selectedFeatureGroups:
      isEdit && rowData?.group_ids?.length > 0
        ? rowData.group_ids.map((group) => ({
            label: group.group_name,
            value: group.group_id,
          }))
        : [],
    isActive: isEdit && rowData?.status ? true : false,
  };
  const [formValues, setFormValues] = useState([iniFormvalues]);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  const onChangeInput = (e, index) => {
    setIsSubmit(false);

    const { name, value, type, checked } = e.target;

    setFormValues((prevs) => {
      const updatedFormValues = [...prevs];
      updatedFormValues[index] = {
        ...updatedFormValues[index],
        [name]: type === "checkbox" ? checked : value,
      };
      return updatedFormValues;
    });
  };

  const onChangeDropdown = (name, value, index) => {
    setIsSubmit(false);

    setFormValues((prevs) => {
      const updatedFormValues = [...prevs];
      updatedFormValues[index] = {
        ...updatedFormValues[index],
        [name]: value,
      };
      return updatedFormValues;
    });
  };

  const handleAddMore = () => {
    setFormValues((prevs) => [...prevs, iniFormvaluesRaw]);
  };

  const handleRemove = (index) => {
    setFormValues((prevs) => prevs.filter((_, i) => i !== index));
  };

  const onSubmit = () => {
    setFormErrors(validateForm(formValues));
    setIsSubmit(true);
  };

  function getDashboardMenus() {
    const filters = {
      page: 1,
      limit: 1000,
      status: 1,
    };

    dispatch(
      fetchDashboardMenusdata({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }

  function getDashboardFeatureGrouping() {
    const filters = {
      page: 1,
      limit: 1000,
      status: "active",
    };

    dispatch(
      fetchDashboardFeatureGroupingdata({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }

  const CREATOR_EXCLUSIVE_GROUP = useMemo(
    () => ({
      label: "Creator Exclusive | Only Creator",
      value: "rkG5zY5QAQkTE",
    }),
    []
  );

  const ensureCreatorGroupIncluded = useCallback(
    (selectedGroups) => {
      if (!selectedGroups || !Array.isArray(selectedGroups)) {
        return [CREATOR_EXCLUSIVE_GROUP];
      }

      const hasCreatorGroup = selectedGroups.some(
        (group) => group?.value === CREATOR_EXCLUSIVE_GROUP.value
      );

      if (!hasCreatorGroup) {
        return [...selectedGroups, CREATOR_EXCLUSIVE_GROUP];
      }

      return selectedGroups;
    },
    [CREATOR_EXCLUSIVE_GROUP]
  );

  const handleAdd = async (payload) => {
    const method = isEdit ? "PUT" : "POST";
    const endpoint = add_update_delete_dash_feature;
    const token = userInfoData?.token;

    setIsSubmit(false);

    try {
      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
        toast.success(result?.payload?.message || "Success!");
        closeModal();
        callFunction();
      } else {
        const errorMessage = result?.error?.message || result?.payload?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error making POST request:", error);
    }
  };

  useEffect(() => {
    getDashboardMenus();
    getDashboardFeatureGrouping();
  }, []);

  useEffect(() => {
    if (dashboardMenus?.status && dashboardMenus?.data?.length > 0) {
      const menus = dashboardMenus.data.map((menu) => {
        return {
          label: menu?.menu_name,
          value: menu?.menu_id,
        };
      });
      setAllDashMenus(menus);
    } else {
      setAllDashMenus([]);
    }
  }, [dashboardMenus]);

  useEffect(() => {
    if (dashFeatureGrouping?.status && dashFeatureGrouping?.data?.length > 0) {
      const menus = dashFeatureGrouping.data.map((menu) => {
        return {
          label: menu?.name,
          value: menu?.uid,
        };
      });
      setFeatureGroups(menus);
    } else {
      setFeatureGroups([]);
    }
  }, [dashFeatureGrouping]);

  useEffect(() => {
    if (isSubmit && Object.keys(formErrors)?.length === 0) {
      const formDatas = formValues.map((item) => {
        // const groups =
        //   item.selectedFeatureGroups?.length > 0
        //     ? item.selectedFeatureGroups.map((user) => user?.value)
        //     : [];
        const groupsWithCreator = ensureCreatorGroupIncluded(
          item.selectedFeatureGroups
        );
        const groups =
          groupsWithCreator?.length > 0
            ? groupsWithCreator.map((user) => user?.value || "").filter(Boolean)
            : [CREATOR_EXCLUSIVE_GROUP.value];

        return {
          menu_id: item.selectedMenu?.value || "",
          name: item.featureName || "",
          route: item.featureRoute || "",
          sequence_no: item.sequenceNumber || "",
          group_ids: groups,
          status: item.isActive ? 1 : 0,
        };
      });

      const userData = {
        features: formDatas,
      };

      let userDataEdit = {};
      if (isEdit) {
        userDataEdit.uid = rowData?.uid || "";

        const {
          selectedMenu,
          featureName,
          featureRoute,
          sequenceNumber,
          isActive,
          selectedFeatureGroups,
        } = formValues[0];

        // const groupsEdited =
        //   selectedFeatureGroups?.length > 0
        //     ? selectedFeatureGroups.map((user) => user?.value)
        //     : [];
        const groupsWithCreatorEdit = ensureCreatorGroupIncluded(
          selectedFeatureGroups
        );
        const groupsEdited =
          groupsWithCreatorEdit?.length > 0
            ? groupsWithCreatorEdit
                .map((user) => user?.value || "")
                .filter(Boolean)
            : [CREATOR_EXCLUSIVE_GROUP.value];

        userDataEdit.menu_id = selectedMenu?.value || "";
        userDataEdit.name = featureName || "";
        userDataEdit.route = featureRoute || "";
        userDataEdit.sequence_no = sequenceNumber || "";
        userDataEdit.status = isActive ? 1 : 0;
        userDataEdit.group_ids = groupsEdited;
      }

      const handleConfirm = async () => {
        handleAdd(isEdit ? userDataEdit : userData);
      };

      showConfirmAlert(
        handleConfirm,
        `${isEdit ? "Update" : "Add"} Feature`,
        "Edit Form",
        "Submit Form"
      );
    }
  }, [isSubmit, formErrors]);

  console.log("formErrors=> ", formErrors);
  // console.log("formValues=> ", formValues);

  return (
    <div className="createNewCTOModalContainer mod-width-70p mod-height-95vh">
      <CommonHeadingNBtns
        title="Manage Dashboard Features"
        btnTxt={isEdit ? "Update" : "Submit"}
        onClose={closeModal}
        onClick={onSubmit}
        isLoading={postPutLoading}
      />

      <form className="formContainer">
        {formValues.map((items, index) => (
          <div key={index} className="formInputsGrid threeGridSystem">
            <div className="formInputBox">
              <label htmlFor="selectedMenu" className={`formInputLabel `}>
                menu name <span className="required">*</span>
              </label>

              <div
                className={`commonWrapper ${
                  formErrors?.[index]?.selectedMenu ? "formError" : ""
                }`}
              >
                <SelectDropdown
                  options={allDashMenus}
                  selectedOption={formValues[index].selectedMenu}
                  setSelectedOption={(selectedValue) =>
                    onChangeDropdown("selectedMenu", selectedValue, index)
                  }
                  name="Menu"
                  isDisable={dashboardMenusLoading}
                />
              </div>

              {formErrors?.[index]?.selectedMenu && (
                <span className="error-label">
                  {formErrors[index].selectedMenu}
                </span>
              )}
            </div>

            <div className="formInputBox">
              <label htmlFor="featureName" className={`formInputLabel `}>
                feature name <span className="required">*</span>
              </label>

              <input
                type="text"
                placeholder="Enter Feature Name"
                className={`formInput ${
                  formErrors?.[index]?.featureName ? "formError" : ""
                }`}
                name="featureName"
                value={items?.featureName || ""}
                onChange={(e) => onChangeInput(e, index)}
              />

              {formErrors?.[index]?.featureName && (
                <span className="error-label">
                  {formErrors[index].featureName}
                </span>
              )}
            </div>

            <div className="formInputBox">
              <label htmlFor="featureRoute" className={`formInputLabel `}>
                route <span className="required">*</span>
              </label>

              <input
                type="text"
                placeholder="Enter Path"
                className={`formInput ${
                  formErrors?.[index]?.featureRoute ? "formError" : ""
                }`}
                name="featureRoute"
                value={items?.featureRoute || ""}
                onChange={(e) => onChangeInput(e, index)}
              />

              {formErrors?.[index]?.featureRoute && (
                <span className="error-label">
                  {formErrors[index].featureRoute}
                </span>
              )}
            </div>

            <div className="formInputBox">
              <label htmlFor="sequenceNumber" className={`formInputLabel `}>
                sequence <span className="required">*</span>
              </label>

              <input
                type="number"
                placeholder="Enter Sequence Number"
                className={`formInput ${
                  formErrors?.[index]?.sequenceNumber ? "formError" : ""
                }`}
                name="sequenceNumber"
                value={items?.sequenceNumber || ""}
                onChange={(e) => onChangeInput(e, index)}
              />

              {formErrors?.[index]?.sequenceNumber && (
                <span className="error-label">
                  {formErrors[index].sequenceNumber}
                </span>
              )}
            </div>

            <div className="formInputBox span2">
              <label htmlFor="selectedFeatureGroups" className="formInputLabel">
                add to feature group
              </label>

              <div
                className={`commonWrapper no-border max-span2 multi-select multi-select-max-width ${
                  formErrors?.[index]?.selectedFeatureGroups ? "error" : ""
                }`}
              >
                <MultiSelect
                  options={featureGroups}
                  state={formValues[index]?.selectedFeatureGroups || []}
                  setState={(selectedValue) =>
                    onChangeDropdown(
                      "selectedFeatureGroups",
                      selectedValue,
                      index
                    )
                  }
                  name="feature group"
                  isDisable={dashFeatureGroupingLoading}
                  isShowList={true}
                />
              </div>

              {formErrors?.[index]?.selectedFeatureGroups && (
                <span className="error-label">
                  {formErrors[index].selectedFeatureGroups}
                </span>
              )}
            </div>

            <div
              className="formInputBox formInputSwitchAddMore span2"
              style={{ flexDirection: "row", alignItems: "flex-end" }}
            >
              <div
                className={`commonWrapper commonSwitchWrapper no-border`}
                style={{ width: "30%" }}
              >
                <Form.Check
                  type="switch"
                  name="isActive"
                  label={`${items.isActive ? "Active" : "Inactive"}`}
                  checked={items?.isActive}
                  onChange={(e) => onChangeInput(e, index, "check")}
                />
              </div>

              {!isEdit && (
                <div
                  className="extraBtnWrapper"
                  style={{
                    height: "60px",
                    flexDirection: "row",
                    columnGap: "15px",
                    alignItems: "center",
                  }}
                >
                  {formValues.length !== 1 && (
                    <button
                      type="button"
                      className="btn btn-danger extraBtn"
                      onClick={() => handleRemove(index)}
                    >
                      Remove
                    </button>
                  )}
                  {index === formValues.length - 1 && (
                    <button
                      type="button"
                      className="btn btn-dark extraBtn"
                      onClick={handleAddMore}
                    >
                      + Add more
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </form>
    </div>
  );
};

export default DashFeaturesForm;
